import { NextPage } from 'next';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import getConfig from 'next/config';
import { ReactElement, ReactNode, useEffect } from 'react';
import {
    ApolloProvider,
    initialAppState,
    useApollo,
    GlobalProvider,
    SubComponentUnion,
    setFeatureFlags,
} from '@honeycomb/data';
import { trackPageViewServer } from '@honeycomb/tracking';
import {
    GoogleTagManager,
    GoogleTagManagerNoScript,
    Layout,
    Platform195,
    Platform195Brand,
    Platform195Props,
    Webtrends,
} from '@honeycomb/ui';
import {
    BreakpointProvider,
    HoneycombThemeProvider,
    LazyImageProvider,
    NETFLIGHTS_THEME_OVERRIDES,
} from '@honeycomb/ui-core';
import { ErrorBoundary } from 'react-error-boundary';
import { v4 } from 'uuid';
import { searchCreatedRouting } from '@honeycomb/routing';
import dynamic from 'next/dynamic';
import { ResponseTapTrack } from 'components/layout/ResponseTapTrack';
import { errorFallback } from '../components/error/errorFallback';
import { Masthead } from '../components/layout/Masthead';
import '../styles/roboto.css';
import '../styles/AzoSansUber.css';

type NextPageWithLayout = NextPage & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

export type GenericSubComponentProps = {
    component: SubComponentUnion;
};

export type GetLayout<P> = (page: React.ReactElement, props: P) => ReactNode;

const { mockingEnabled } = getConfig()?.publicRuntimeConfig || { mockingEnabled: '' };
if (mockingEnabled === 'true') {
    import('@honeycomb/data').then((mod) => mod.mockData());
}

const platform195Props: Platform195Props = {
    sizeArray: [
        {
            screenSize: [1024, 0],
            adSize: [
                [970, 250],
                [728, 90],
            ],
        },
        { screenSize: [768, 0], adSize: [[728, 90]] },
        {
            screenSize: [360, 0],
            adSize: [
                [300, 250],
                [320, 100],
            ],
        },
    ],
    adSlots: [
        {
            name: 'HO_HT_SRP/SRPAD1',
            sizes: [
                [320, 100],
                [728, 90],
            ],
            id: 'div-gpt-ad-1934534-16',
        },
        {
            name: 'HO_HT_SRP/SRPAD2',
            sizes: [
                [320, 100],
                [728, 90],
            ],
            id: 'div-gpt-ad-1934534-17',
        },
        {
            name: 'DEST/Hero',
            sizes: [
                [970, 250],
                [728, 90],
                [300, 250],
            ],
            id: 'div-gpt-ad-1942499-1',
        },
    ],
    platform195Brand: Platform195Brand.NetFlights,
};

const Footer = dynamic(() => import('../components/layout/Footer').then((module) => module.Footer), {
    ssr: false,
}) as React.FunctionComponent;

export default function App({ Component, pageProps }: AppPropsWithLayout) {
    // Individual pages can specify a custom layout by implementing a getLayout property
    // See https://nextjs.org/docs/basic-features/layouts for more detail.

    const getLayout: GetLayout<typeof pageProps> =
        Component.getLayout ??
        ((page, props) => (
            <Layout masthead={<Masthead />} footer={<Footer />} {...props}>
                {page}
            </Layout>
        ));

    // Must be called before any other initializeApollo call (inc. that via initialAppState) to ensure userContext is added to apolloClient singleton
    // TODO: Refactor initialAppState to resolve this flakiness
    const apolloClient = useApollo(pageProps);

    const query = pageProps.context?.query || null;

    if (query) {
        setFeatureFlags(pageProps.configSettings, query);
    }

    // Checks the query params and sets up the reactive vars, must happen client-side
    useEffect(() => {
        initialAppState(query, searchCreatedRouting);
    }, [query]);

    const isMobile = pageProps?.deviceDetection?.isMobile;
    const isTablet = pageProps?.deviceDetection?.isTablet;
    const ssrPredictedWidth = isTablet ? 768 : isMobile ? 375 : 1024;

    if (typeof window === 'undefined') {
        trackPageViewServer(pageProps);
    }

    return (
        <GlobalProvider
            resourceStrings={pageProps.resourceStrings}
            configSettings={pageProps.configSettings}
            globalContents={pageProps.globalContents}
            currentContext={pageProps.currentContext}
            deviceInfo={pageProps.deviceDetection}
        >
            <HoneycombThemeProvider
                theme={NETFLIGHTS_THEME_OVERRIDES}
                cultureCode={pageProps.currentContext?.cultureCode}
            >
                <BreakpointProvider ssrPredictedWidth={ssrPredictedWidth}>
                    <ApolloProvider client={apolloClient}>
                        <Head>
                            {/* https://nextjs.org/docs/messages/no-document-viewport-meta */}
                            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                        </Head>
                        <LazyImageProvider>
                            <GoogleTagManagerNoScript />
                            <ResponseTapTrack />

                            {getLayout(
                                <ErrorBoundary
                                    resetKeys={[pageProps.context?.req.url]}
                                    fallbackRender={errorFallback(v4())}
                                >
                                    <GoogleTagManager />
                                    <Platform195 {...platform195Props} />
                                    <Webtrends path={pageProps.context?.req.url} />
                                    <Component {...pageProps} />
                                </ErrorBoundary>,
                                pageProps
                            )}
                        </LazyImageProvider>
                    </ApolloProvider>
                </BreakpointProvider>
            </HoneycombThemeProvider>
        </GlobalProvider>
    );
}
